import { AddCircleOutline, Delete, Edit, RemoveRedEye } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { invalidateSchemaForm, useDeleteFormSchemaById, useGetAllFormSchema } from 'src/api/form-schema'
import { AppRoutesNavigation } from 'src/app-routes'
import NoReports from 'src/assets/svg/no-reports.svg'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import GenericTable from 'src/components/table/GenericTable'
import { SchemaType } from 'src/generated-types/form-schema'
import { useCustomerCtx, useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import Form from '../form/Form'

export const ChecklistTemplatesContainer = () => {
  const { t: translate } = useTranslation(['checklist', 'common', 'admin-sidebar', 'common-api-message'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { customer } = useCustomerCtx()
  const navigate = useNavigate()
  const theme = useTheme()

  const { data: checkLists } = useGetAllFormSchema(customer.id, { schemaType: SchemaType.Checklist })
  const deleteFormSchema = useDeleteFormSchemaById(customer.id)

  const STATIC_COLUMNS = [
    {
      field: 'name',
      headerName: translate('name', { ns: 'common' }),
      flex: 5,
    },
    {
      field: 'description',
      headerName: translate('description', { ns: 'common' }),
      flex: 5,
    },
    {
      field: 'isActive',
      headerName: translate('isActive', { ns: 'common' }),
      flex: 2,
      renderCell: params =>
        params.row.isActive ? translate('yes', { ns: 'common' }) : translate('no', { ns: 'common' }),
    },
    {
      field: 'actions',
      headerName: translate('actions', { ns: 'common' }),
      flex: 2,
      renderCell: params => (
        <Box width={200} display={'flex'} flexDirection={'row'} justifyContent={'space-evenly'}>
          <Edit
            className={'cursor-pointer'}
            onClick={() => {
              navigate(AppRoutesNavigation.Customer.editChecklistTemplate(customer?.id, params.row?.id))
            }}
          />

          <RemoveRedEye
            className={'cursor-pointer'}
            onClick={() => {
              showDialog(
                Form,
                {
                  componentProps: {
                    disabled: false,
                    form: undefined,
                    onDrop: () => { },
                    addLayout: () => { },
                    deleteLayout: () => { },
                    selectFormField: () => { },
                    layouts: JSON.parse(params.row?.version?.layout || '[]'),
                    title: params?.row?.name,
                    onRearrange: undefined,
                    pop: () => { },
                    cancelEnabled: true,
                  },
                  opts: {
                    maxWidth: 'md',
                    fullWidth: true,
                  },
                  isAutoCloseDisabled: true,
                },
                undefined,
                true,
              )
            }}
          />
          <Delete
            className={'cursor-pointer'}
            onClick={() => {
              showConfirmDialog(
                translate('delete-checklist-title', { ns: 'checklist' }),
                translate('delete-checklist-confirm', { ns: 'checklist' }),
                {
                  acceptText: translate('yes', { ns: 'common' }),
                  cancelText: translate('no', { ns: 'common' }),
                },
                async pop => {
                  await deleteFormSchema.mutateAsync(params.row?.id)
                  invalidateSchemaForm.getAllFormSchema(customer.id)
                  toast.success(translate('successfullyRemoved', { ns: 'common-api-message' }))
                  pop()
                },
              )
            }}
          />
        </Box>
      ),
    },
  ]
  const NoDataComponent = () => (
    <Box height="100%">
      <EmptyStatusCard
        imageWidth={'90px'}
        image={NoReports}
        header={translate('no-templates-title', { ns: 'checklist' })}
        imageContainerClasses="pb-4 pt-2"
        contentTopMargin="10px"
        headerFontSize="18px"
        contentFontSize="14px"
        contentClasses="mt-2"
        height="100%"
      />
    </Box>
  )
  return (
    <>
      <CommonPageLayout
        titleSection={{ title: translate('checklist-templates', { ns: 'admin-sidebar' }) }}
        topAction={[
          {
            buttonText: translate('add-checklist', { ns: 'checklist' }),
            buttonIcon: <AddCircleOutline />,
            onClick: () => {
              navigate(AppRoutesNavigation.Customer.addChecklistTemplate(customer?.id))
            },
            variant: 'outlined',
            shadowWeight: null,
            border: 0,
          },
        ]}
      >
        <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
          <GenericTable
            id="checklist-templates"
            columns={STATIC_COLUMNS}
            data={checkLists || []}
            autoHeight={false}
            height={window.innerHeight - 230}
            noDataComponent={NoDataComponent}
          />
        </Box>
      </CommonPageLayout>
    </>
  )
}
